export interface AufstellerInfo {
  firma?: string;
  adresse?: string;
  telefon?: string;
  /** Firmenlogo als Data-URL — der Plankopf druckt es links neben den Firmendaten. */
  logo_url?: string;
  email?: string;
  web?: string;
}

/** Ein Verantwortlicher im Plankopf: der Koordinator selbst oder seine Vertretung. */
export interface PlanVerantwortlicher {
  name: string;
  rolle?: string;      // 'SiGeKo' | 'Vertretung' — frei, weil der Plankopf es woertlich druckt
  telefon?: string;
  email?: string;
}

/**
 * Darstellung des Plans (Spalte `sige_projekte.darstellung`, Migration Nr. 62).
 *
 * Farben als Hex (`#1f4e89`). Leer = Vorgabe des Generators; Altplaene ohne
 * Spalte drucken deshalb unveraendert weiter.
 */
export interface PlanDarstellung {
  /** Zeitbalken der unkritischen Arbeitsschritte. */
  balken?: string;
  /** Zeitbalken der als kritisch markierten Schritte — die Signalfarbe. */
  balkenKritisch?: string;
  /** Kopfband der Matrix und die Rahmen der Randspalten-Boxen. */
  kopfBand?: string;
  /** Koordinator + Vertretung, wie sie im Plankopf unten rechts stehen. */
  verantwortliche?: PlanVerantwortlicher[];
  /**
   * Grundschrift der Matrix in pt (Regler in Vorschau und Stempelfeld). Alle
   * uebrigen Schriften des Blatts und ihre Abstaende wachsen im selben
   * Verhaeltnis mit. Leer = Vorgabe der Firma (`sigePlanVorlagen`, sonst
   * `PLAN_SCHRIFT.vorgabe`). Kein SQL noetig: der CHECK aus Nr. 62 prueft nur
   * die Farb- und Verantwortlichen-Schluessel.
   */
  schriftPt?: number;
  /**
   * Kleberand oben und unten in mm: freier Streifen an der oberen und unteren
   * Blattkante, auf dem nichts gedruckt wird. Leer = `PLAN_KLEBERAND.vorgabe`,
   * 0 = nur der normale Aussenrand des Generators. Kein SQL noetig (wie
   * `schriftPt`).
   */
  kleberandMm?: number;
  /**
   * `false` = Bauzeitenplan liegt noch nicht vor (Planungsphase): der Plan
   * druckt die Zeitspalte schmal, ohne Monatsraster, Balken und Daten, mit
   * dem Hinweis, dass die Zeitraeume mit der Fortschreibung folgen. Leer =
   * Zeitachse wie bisher. Gespeichert wird nur `false` (siehe
   * `mitPlanZeitachse`). Kein SQL noetig (wie `schriftPt`).
   */
  zeitachse?: boolean;
}

/** Ob der Plan eine Zeitachse druckt — nur ein ausdrueckliches `false` schaltet sie ab. */
export const planHatZeitachse = (d?: PlanDarstellung | null): boolean => d?.zeitachse !== false;

/**
 * Darstellung mit/ohne Zeitachse. Wie bei Schrift und Kleberand wird die
 * Vorgabe (Zeitachse an) nicht gespeichert, sondern der Schluessel entfernt.
 */
export const mitPlanZeitachse = (d: PlanDarstellung | null | undefined, an: boolean): PlanDarstellung => {
  const { zeitachse: _alt, ...rest } = d || {};
  return an ? rest : { ...rest, zeitachse: false };
};

/**
 * Grenzen des Schriftreglers. Bis Oktober 2026 druckte die Matrix fest in
 * 6 pt — auf dem Aushang nur aus der Naehe lesbar. `vorgabe` ist die
 * Werkseinstellung; die Firma kann eine eigene Vorgabe setzen
 * (services/sigePlanVorlagen.ts), die Grenzen gelten fuer beide.
 */
export const PLAN_SCHRIFT = { vorgabe: 10, min: 6, max: 14, schritt: 0.5 } as const;

/** Unlesbare Vorgabe → Werkseinstellung, sonst in die Reglergrenzen geklemmt. */
const gueltigeSchriftVorgabe = (vorgabe: number): number =>
  typeof vorgabe === 'number' && Number.isFinite(vorgabe) && vorgabe > 0
    ? Math.min(PLAN_SCHRIFT.max, Math.max(PLAN_SCHRIFT.min, vorgabe))
    : PLAN_SCHRIFT.vorgabe;

/**
 * Wirksame Grundschrift eines Plans: Vorgabe, wenn nichts oder Unlesbares
 * gespeichert ist, sonst in die Reglergrenzen geklemmt.
 *
 * `vorgabe` ist die Firmenvorgabe (useSigePlanVorlagen().standardSchriftPt).
 * Ohne Angabe gilt die Werkseinstellung — so bleiben Aufrufer ohne
 * Firmenbezug (Tests, Generator) unveraendert.
 */
export const planSchriftPt = (d?: PlanDarstellung | null, vorgabe: number = PLAN_SCHRIFT.vorgabe): number => {
  const basis = gueltigeSchriftVorgabe(vorgabe);
  const v = d?.schriftPt;
  if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return basis;
  return Math.min(PLAN_SCHRIFT.max, Math.max(PLAN_SCHRIFT.min, v));
};

/**
 * Darstellung mit neuer Grundschrift. Die Vorgabe wird NICHT gespeichert,
 * sondern der Schluessel entfernt — wie bei den Farben: sonst friert der
 * erste Reglerzug die heutige Vorgabe fuer immer ein.
 *
 * Verglichen wird mit der FIRMENvorgabe (`vorgabe`): ein Plan, der auf ihr
 * steht, traegt keinen eigenen Wert und folgt damit einer spaeter
 * geaenderten Vorgabe. Ohne Angabe gilt die Werkseinstellung.
 */
export const mitPlanSchrift = (
  d: PlanDarstellung | null | undefined,
  pt: number,
  vorgabe: number = PLAN_SCHRIFT.vorgabe,
): PlanDarstellung => {
  const { schriftPt: _alt, ...rest } = d || {};
  const basis = gueltigeSchriftVorgabe(vorgabe);
  const wert = planSchriftPt({ schriftPt: pt }, basis);
  return wert === basis ? rest : { ...rest, schriftPt: wert };
};

/**
 * Kleberand oben und unten. Seit Oktober 2026 hat jeder Plan standardmaessig
 * 20 mm, damit er sich aufkleben laesst, ohne dass Titel oder Fusszeile unter
 * dem Klebestreifen liegen. Je Plan im Stempelfeld einstellbar.
 */
export const PLAN_KLEBERAND = { vorgabe: 20, min: 0, max: 50 } as const;

/**
 * Wirksamer Kleberand eines Plans: Vorgabe, wenn nichts oder Unlesbares
 * gespeichert ist, sonst in die Grenzen geklemmt. 0 ist ein gueltiger Wert
 * (kein Kleberand).
 */
export const planKleberandMm = (d?: PlanDarstellung | null): number => {
  const v = d?.kleberandMm;
  if (typeof v !== 'number' || !Number.isFinite(v)) return PLAN_KLEBERAND.vorgabe;
  return Math.min(PLAN_KLEBERAND.max, Math.max(PLAN_KLEBERAND.min, v));
};

/**
 * Darstellung mit neuem Kleberand (ganze mm). Wie bei der Schrift wird die
 * Vorgabe nicht gespeichert, sondern der Schluessel entfernt.
 */
export const mitPlanKleberand = (d: PlanDarstellung | null | undefined, mm: number): PlanDarstellung => {
  const { kleberandMm: _alt, ...rest } = d || {};
  const wert = planKleberandMm({ kleberandMm: Number.isFinite(mm) ? Math.round(mm) : mm });
  return wert === PLAN_KLEBERAND.vorgabe ? rest : { ...rest, kleberandMm: wert };
};

/** Vorgabefarben — Blau fuer den Normalfall, Rot bleibt dem kritischen Schritt. */
export const DEFAULT_PLAN_FARBEN = {
  balken: '#1f4e89',
  balkenKritisch: '#be2d23',
  kopfBand: '#21405f',
} as const;

/** Fertige Paletten fuer die Auswahl im Stempelfeld. */
export const FARB_PALETTEN: { name: string; farben: Required<Pick<PlanDarstellung, 'balken' | 'balkenKritisch' | 'kopfBand'>> }[] = [
  { name: 'Blau (Vorgabe)', farben: { balken: '#1f4e89', balkenKritisch: '#be2d23', kopfBand: '#21405f' } },
  { name: 'Petrol', farben: { balken: '#0f766e', balkenKritisch: '#c2410c', kopfBand: '#134e4a' } },
  { name: 'Grau', farben: { balken: '#4b5563', balkenKritisch: '#b91c1c', kopfBand: '#374151' } },
  { name: 'Rot (alt)', farben: { balken: '#cd2323', balkenKritisch: '#8c0000', kopfBand: '#21405f' } },
];

export interface SigeProject {
  id: string;
  project_id?: string;
  bauherr_name: string;
  bauherr_adresse: string;
  baumassnahme_name: string;
  baumassnahme_adresse: string;
  plan_nr: string;
  plan_datum: string;
  plan_inhalt: string;
  status: 'entwurf' | 'geprueft' | 'ueberarbeitet';
  bearbeitet_von_id?: string;
  bearbeitet_datum?: string;
  bearbeitet_name?: string;
  geprueft_von_id?: string;
  geprueft_datum?: string;
  geprueft_name?: string;
  unterschrift_url?: string;
  /**
   * Plotterbreite in mm. `null` nur beim LESEN (Altbestand): gilt dann als
   * Standardbreite der Firma (projektFuerDruck). Geschrieben wird immer eine
   * Zahl (plotBreiteZumSpeichern) — ob die Spalte NULL zulaesst, ist nicht
   * belegt (services/sigePlanVorlagen.ts).
   */
  plot_breite_mm?: number | null;
  sigeko_name?: string;
  sigeko_mobil?: string;
  sigeko_email?: string;
  aufsteller?: AufstellerInfo;
  /** Farben und Plankopf-Verantwortliche (Migration Nr. 62). */
  darstellung?: PlanDarstellung;
  bauherr_logo_url?: string;
  zeitraum_von?: string;
  zeitraum_bis?: string;
  hauptabschnitte?: string[];
  /** Vom Anwender vergebener Versionsname (Migration `sige_versions.sql`). */
  version_label?: string | null;
  created_at?: string;
}

// Hauptabschnitte gemäß Muster-SiGePlan (Reihenfolge = Plan-Reihenfolge)
export const HAUPTABSCHNITTE = [
  'Baustellenvorbereitung',
  'Baustelleneinrichtung',
  'gemeinsam genutzte Einrichtung: VERKEHRSWEGE',
  'gemeinsam genutzte Einrichtung: LAGERPLÄTZE',
  'gemeinsam genutzte Einrichtung: ARBEITSPLÄTZE',
  'gemeinsam genutzte Einrichtung: UNTERKÜNFTE',
  'gemeinsam genutzte Einrichtung: MASCHINEN, GERÄTE, GERÜSTE',
  'gemeinsam genutzte Einrichtung: ARBEITSSCHUTZ',
  'ARBEITSVERFAHREN mit Gefährdung anderer Gewerke',
  'Besondere Arbeitsbedingungen: Lärm, Hitze, Biostoffe',
] as const;

export interface Arbeitsschritt {
  id: string;
  sige_projekt_id: string;
  bezeichnung: string;
  gewerk: string;
  zeitraum_von?: string;
  zeitraum_bis?: string;
  raeumliche_zuordnung?: string;
  sortierung: number;
  hauptabschnitt?: string;
  schutzmassnahmen?: string[];
  vorschriften?: string[];
  /**
   * Mitgeltende Dokumente (Spalte `sige_arbeitsschritte.mitgeltende_dokumente`,
   * Migration Nr. 156): Gutachten, Statik, Anweisungen … — Langform, wie im
   * Katalog `src/services/mitgeltendeDokumente`; freie Eintraege erlaubt. Der
   * Plan druckt sie unter den Vorschriften in Spalte 3.
   */
  mitgeltende_dokumente?: string[];
  hinweis_code?: string;        // Legacy (einzeln) — neu: hinweis_codes
  hinweis_codes?: string[];     // BG BAU Bausteine, mehrere möglich — NICHT mehr im Plan gedruckt, nur noch fürs Handbuch (PSA/Sicherheitszeichen)
  kritisch?: boolean;
  kategorie?: string;
  koordinierungsbedarf?: string;
  zustaendig_erstellung?: string;
  zustaendig_kontrolle?: string;
  zustaendig_instandhaltung?: string;
}

export interface Gefaehrdung {
  id: string;
  arbeitsschritt_id: string;
  bezeichnung: string;
  kategorie: string;
  prioritaet: 'hoch' | 'mittel' | 'niedrig';
}

export interface Schutzmassnahme {
  id: string;
  gefaehrdung_id: string;
  beschreibung: string;
  verantwortlich: string;
  regelwerk_referenz?: string;
}

export type BlockTyp =
  | 'ersthelfer'
  | 'notrufplan'
  | 'notrufnummern'
  | 'piktogrammfeld'
  | 'gefaehrdungsbeurteilung'
  | 'grundsatz'
  | 'legende'
  | 'risk_assessment'
  | 'besonderheiten'
  | 'stempelfeld'
  | 'benutzerdefiniert';

export interface NotrufKontakt {
  name?: string;
  adresse?: string;
  telefon?: string;
  entfernung?: number; // Luftlinie in km (auto ermittelt, optional)
  /**
   * Koordinaten der Stelle. Nur automatisch ermittelte Eintraege haben sie —
   * sie sind die Voraussetzung dafuer, den Eintrag spaeter zu bestaetigen und
   * in die geprueften Notfallstellen zu uebernehmen (services/notfallStellenDb.ts).
   */
  lat?: number;
  lng?: number;
  /** Herkunft des OSM-Objekts, damit dieselbe Stelle wiedererkannt wird. */
  osmTyp?: 'node' | 'way' | 'relation';
  osmId?: number;
  /** 'db' = aus den geprueften Stellen uebernommen, 'osm' = frisch gesucht. */
  quelle?: 'db' | 'osm';
  /** Prueferaum: wer hat die Stelle wann bestaetigt (aus notfall_stellen). */
  geprueftVon?: string;
  geprueftAm?: string;
  /** Vorbehalt aus der Bewertung, z. B. "D-Arzt-Zulassung pruefen". */
  hinweis?: string;
}

export interface NotrufplanInhalt {
  krankenhaus?: NotrufKontakt;
  durchgangsarzt?: NotrufKontakt;
  augenarzt?: NotrufKontakt;
  apotheke?: NotrufKontakt;
  ersthelfer?: { name: string; firma?: string }[];
  kartenBild?: string;            // PNG/JPEG-DataURL des Kartenausschnitts mit Markern
  geo?: { lat: number; lng: number }; // Geokoordinaten der Baumaßnahme (Projekt-Marker)
  /**
   * Ersatzadresse, wenn die Baumaßnahme keine eigene Adresse trägt (Bauvorhaben
   * „im Feld", noch ohne Hausnummer). Sie ist NUR der Aufhänger für Suche und
   * Karte — der Plan weist sie deshalb unter der Karte aus, damit niemand die
   * gezeichnete Lage für die Baustelle hält.
   */
  ersatzAdresse?: string;
}

export interface LegendeEintrag {
  kuerzel: string;
  bedeutung: string;
}

// Piktogramme: key + Darstellungstyp für PDF-Rendering
export type PiktogrammTyp = 'gebot' | 'warnung' | 'verbot' | 'rettung' | 'brandschutz';
export interface PiktogrammDef {
  key: string;
  label: string;
  typ: PiktogrammTyp;
  symbol: string;   // Kurzsymbol/Text — Fallback, falls kein SVG vorhanden
  iso?: string;     // ISO-7010-Code → echtes SVG unter /sicherheitszeichen/iso7010/ISO_7010_<iso>.svg
}

export const PIKTOGRAMME: PiktogrammDef[] = [
  { key: 'helm',              label: 'Schutzhelm tragen',         typ: 'gebot',   symbol: 'H',  iso: 'M014' },
  { key: 'schuhe',            label: 'Sicherheitsschuhe tragen',  typ: 'gebot',   symbol: 'S',  iso: 'M008' },
  { key: 'warnweste',         label: 'Warnweste tragen',          typ: 'gebot',   symbol: 'W',  iso: 'M015' },
  { key: 'brille',            label: 'Schutzbrille tragen',       typ: 'gebot',   symbol: 'B',  iso: 'M004' },
  { key: 'gehoerschutz',      label: 'Gehörschutz tragen',        typ: 'gebot',   symbol: 'G',  iso: 'M003' },
  { key: 'handschuhe',        label: 'Schutzhandschuhe tragen',   typ: 'gebot',   symbol: 'HS', iso: 'M009' },
  { key: 'psaga',             label: 'PSAgA benutzen',            typ: 'gebot',   symbol: 'A',  iso: 'M018' },
  { key: 'absturz',           label: 'Warnung vor Absturz',       typ: 'warnung', symbol: '!',  iso: 'W008' },
  { key: 'schwebende_last',   label: 'Warnung schwebende Last',   typ: 'warnung', symbol: '!',  iso: 'W015' },
  { key: 'baustellenverkehr', label: 'Warnung Baustellenverkehr', typ: 'warnung', symbol: '!',  iso: 'W014' },
  { key: 'elektrisch',        label: 'Warnung elektr. Spannung',  typ: 'warnung', symbol: 'Z',  iso: 'W012' },
  { key: 'zutritt_verboten',  label: 'Zutritt verboten',          typ: 'verbot',  symbol: 'X',  iso: 'P006' },
];

// Standard-Auswahl, wenn im Piktogramm-Block nichts gesetzt ist
export const DEFAULT_PIKTO_KEYS = ['helm', 'schuhe', 'warnweste', 'gehoerschutz', 'absturz', 'schwebende_last'];

// Pfad zum echten ISO-7010-PNG — aus Supabase Storage (öffentlicher Bucket).
// Fällt auf den lokalen public/-Ordner zurück, falls VITE_SUPABASE_URL fehlt.
const SB_URL = (import.meta as any).env?.VITE_SUPABASE_URL || '';
export const piktoPfad = (iso: string) =>
  SB_URL
    ? `${SB_URL}/storage/v1/object/public/sige-sicherheitszeichen/ISO_7010_${iso}.png`
    : `/sicherheitszeichen/iso7010/ISO_7010_${iso}.png`;

// Lokaler (same-origin) Pfad — garantiert taint-frei beim Rastern auf Canvas.
// Dient als Fallback, falls die Storage-URL beim PDF-Rastern den Canvas „taintet".
export const piktoPfadLokal = (iso: string) => `/sicherheitszeichen/iso7010/ISO_7010_${iso}.png`;

// --- Beliebige ISO-7010-Zeichen wählbar (projektübergreifend) ---------------
// Ein icons-Eintrag kann ein Legacy-Key ('helm') ODER ein ISO-Code ('M014') sein.

const ISO_RE = /^[PMWEF]\d{3}[A-Za-z]?$/;
export const istIsoCode = (s: string): boolean => ISO_RE.test(s);

// ISO-7010-Präfix → Darstellungstyp (Form/Farbe im PDF)
export const isoTyp = (code: string): PiktogrammTyp => {
  switch (code[0]?.toUpperCase()) {
    case 'M': return 'gebot';        // mandatory  → blauer Kreis
    case 'W': return 'warnung';      // warning    → gelbes Dreieck
    case 'P': return 'verbot';       // prohibition→ roter Kreis
    case 'E': return 'rettung';      // safe cond. → grünes Quadrat
    case 'F': return 'brandschutz';  // fire safety→ rotes Quadrat
    default:  return 'warnung';
  }
};

// Deutsche Labels für häufige ISO-7010-Zeichen (Ergänzung zu PIKTOGRAMME).
// Fallback: "ISO 7010 <Code>".
export const ISO_LABELS: Record<string, string> = {
  M001: 'Allgemeines Gebotszeichen',  M002: 'Anleitung beachten',
  M003: 'Gehörschutz tragen',         M004: 'Augenschutz tragen',
  M008: 'Fußschutz tragen',           M009: 'Handschutz tragen',
  M010: 'Schutzkleidung tragen',      M013: 'Gesichtsschutz tragen',
  M014: 'Kopfschutz tragen',          M015: 'Warnweste tragen',
  M016: 'Maske benutzen',             M017: 'Atemschutz tragen',
  M018: 'Auffanggurt benutzen',       M020: 'Rückhaltesystem benutzen',
  W008: 'Warnung vor Absturzgefahr',  W012: 'Warnung vor elektr. Spannung',
  W014: 'Warnung vor Flurförderzeugen', W015: 'Warnung vor schwebender Last',
  P006: 'Zutritt für Unbefugte verboten',
};

export interface PiktoAufgeloest { iso: string; label: string; typ: PiktogrammTyp; symbol: string; }

// Löst einen icons-Eintrag (Legacy-Key ODER ISO-Code) für Anzeige/PDF auf.
export const aufloesePikto = (entry: string): PiktoAufgeloest | null => {
  const def = PIKTOGRAMME.find(p => p.key === entry);
  if (def?.iso) return { iso: def.iso, label: def.label, typ: def.typ, symbol: def.symbol };
  if (def)      return { iso: '', label: def.label, typ: def.typ, symbol: def.symbol };
  if (istIsoCode(entry)) {
    return { iso: entry, label: ISO_LABELS[entry] || `ISO 7010 ${entry}`, typ: isoTyp(entry), symbol: entry[0] };
  }
  return null;
};

// Ist der ISO-Code in der icons-Liste aktiv (egal ob als Code oder Legacy-Key gespeichert)?
export const istCodeAktiv = (icons: string[], code: string): boolean =>
  icons.some(e => aufloesePikto(e)?.iso === code);

// Entfernt jeden Eintrag, der auf den ISO-Code zeigt (Code ODER zugehöriger Legacy-Key).
export const entferneCode = (icons: string[], code: string): string[] =>
  icons.filter(e => aufloesePikto(e)?.iso !== code);

export interface SigeBlock {
  id: string;
  sige_projekt_id: string;
  typ: BlockTyp;
  titel: string;
  inhalt: any;
  pos_x: number;
  pos_y: number;
  breite: number;
  hoehe: number;
}

// Globale Kataloge (Migration v3) — Einträge gelten für alle Nutzer/Projekte
export interface HauptabschnittEintrag {
  id: string;
  name: string;
  sortierung: number;
}

export interface KatalogArbeit {
  id?: string; // ohne id = statischer Muster-Eintrag
  bezeichnung: string;
  hauptabschnitt?: string;
  gewerk?: string;
  schutzmassnahmen?: string[];
  vorschriften?: string[];
  /** Wie `Arbeitsschritt.mitgeltende_dokumente` (Spalte `sige_arbeiten_katalog.mitgeltende_dokumente`, Nr. 156). */
  mitgeltende_dokumente?: string[];
  hinweis_code?: string;
  hinweis_codes?: string[];
  koordinierungsbedarf?: string;
  zustaendig_erstellung?: string;
  zustaendig_kontrolle?: string;
  zustaendig_instandhaltung?: string;
}

export interface GefaehrdungsKatalog {
  id: string;
  bezeichnung: string;
}

export interface KatalogMassnahme {
  id: string;
  katalog_id: string;
  gefaehrdung_bezeichnung: string;
  kategorie: string;
  standard_massnahmen: string[];
  regelwerk_referenzen: string[];
}

// ─── Einweisung in den SiGe-Plan ────────────────────────────────────────────
// Der SiGeKo weist auf der Baustelle mehrfach in den Plan ein: Aufbau des
// Plans + wesentliche Gefahren, als Stichpunkte auf 1–2 Seiten, darunter die
// Unterschriftenliste. Pro Projekt gibt es MEHRERE Einweisungen.
//
// Die Punkte werden regelbasiert aus dem Plan vorgeschlagen
// (src/services/sigeEinweisung.ts) und liegen danach als EIGENE Kopie am
// Datensatz — nicht als Verweis in den Plan. Grund: beim Abschliessen wird das
// Blatt EINGEFROREN, damit ein spaeterer Planwechsel das unterschriebene
// Dokument nicht nachtraeglich verfaelscht.

/** Eine Ueberschrift des Einweisungsblattes mit ihren Stichpunkten. */
export interface EinweisungGruppe {
  /** Stabiler Schluessel ('aufbau', 'gefahren', …) — traegt keine Anzeige. */
  id: string;
  titel: string;
  punkte: string[];
}

/** Ein Teilnehmer der Einweisung samt (optionaler) digitaler Unterschrift. */
export interface EinweisungTeilnehmer {
  id: string;
  name: string;
  firma?: string;
  /** DataURL des Canvas-Strichs (image/png). Fehlt = auf Papier unterschrieben. */
  unterschrift?: string;
  /** ISO-Zeitpunkt der Unterschrift. */
  unterschriebenAm?: string;
  /** Funktion auf der Baustelle (Polier, Bauleiter, Monteur …) — Freitext, darf fehlen. */
  funktion?: string;
  /**
   * Woher die Zeile stammt. Fehlt = auf dem Geraet erfasst (Bestand).
   * 'online' = ueber den Link/QR eingetragen (Tabelle sige_einweisung_teilnahmen),
   * nur in der VEREINIGTEN Liste (vereinigeTeilnehmer) — nie im jsonb der Einweisung.
   */
  quelle?: 'geraet' | 'online';
}

export type EinweisungStatus = 'entwurf' | 'abgeschlossen';

/**
 * 'digital' = in der App gefuehrt und unterschrieben.
 * 'papier'  = auf der Baustelle auf Papier durchgefuehrt, der Scan haengt als
 *             Datei daran. Der Datensatz ist trotzdem vollwertig — er wird
 *             gezaehlt, abgerechnet und ausgewertet wie jeder andere.
 */
export type EinweisungQuelle = 'digital' | 'papier';

/**
 * Eine Einweisung (Tabelle `sige_einweisungen`, supabase/sige_einweisungen.sql).
 *
 * Feldnamen in snake_case wie der uebrige SiGe-Plan-Zweig (SigeProject,
 * Arbeitsschritt, SigeBlock) — die Zeile geht unveraendert nach Supabase.
 */
export interface SigeEinweisung {
  id: string;
  /** Das App-Projekt (projects.id), wie bei `SigeProject.project_id`. */
  project_id: string;
  /**
   * Die PLANVERSION, in die eingewiesen wurde (`sige_projekte.id`). NULL-bar:
   * eine nachgetragene Papier-Einweisung kennt sie nicht immer.
   */
  sige_projekt_id?: string | null;
  /** Laufende Nummer JE PROJEKT, ab 1. Vergabe: naechsteEinweisungsNummer. */
  nummer: number;
  titel?: string | null;
  /** Tag der Einweisung, ISO (YYYY-MM-DD). */
  datum: string;
  /** Uhrzeit als Text ('07:30') — bewusst kein Zeitzonen-Typ. */
  uhrzeit?: string | null;
  ort?: string | null;
  dauer_min?: number | null;
  durchfuehrender_name?: string | null;
  durchfuehrender_id?: string | null;
  /** Planbezug, EINGEFROREN: Nummer und Stand des Plans zum Zeitpunkt. */
  plan_nr?: string | null;
  plan_stand?: string | null;
  punkte: EinweisungGruppe[];
  teilnehmer: EinweisungTeilnehmer[];
  /** Blanko-Unterschriftszeilen im PDF fuer Nachzuegler. */
  leerzeilen: number;
  bemerkung?: string | null;
  status: EinweisungStatus;
  quelle: EinweisungQuelle;
  /** Pfad im Bucket `sige-einweisungen` (nur bei quelle='papier'). */
  dokument_pfad?: string | null;
  /** Urspruenglicher Dateiname des Scans — der Pfad traegt nur eine UUID. */
  dokument_name?: string | null;
  /** Gesetzt, sobald die Einweisung auf eine LV-Position gebucht wurde. */
  gebucht_am?: string | null;
  gebuchte_position_id?: string | null;
  gebuchte_menge?: number | null;
  /** Anzeigename der Firma (Mandant), denormalisiert wie im Bestand. */
  company_name?: string | null;
  /**
   * Der Link zur Online-Einweisung (`#/einweisung/<token>`). NULL = kein Link.
   * Wird NUR ueber die RPC `sige_einweisung_link_setzen` gesetzt, nie per Update.
   */
  link_token?: string | null;
  /** Optionales Ablaufdatum des Links (timestamptz). NULL = ohne Ablauf. */
  link_aktiv_bis?: string | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * Eine Online-Teilnahme (Tabelle `sige_einweisung_teilnahmen`, Migration Nr. 97).
 *
 * EIGENE Tabelle, eine Zeile je Person — nicht im jsonb `teilnehmer` der
 * Einweisung: das automatische Speichern des Editors schreibt `teilnehmer`
 * als Ganzes zurueck und wuerde eine angehaengte Online-Unterschrift
 * ueberschreiben. Geschrieben wird nur ueber die anonyme RPC; der Innendienst
 * liest. Loeschen ist nicht vorgesehen (Nachweis).
 */
export interface EinweisungOnlineTeilnahme {
  id: string;
  einweisung_id: string;
  project_id: string;
  vorname: string;
  nachname: string;
  funktion?: string | null;
  firma: string;
  /** PNG-DataURL der Unterschrift. */
  unterschrift: string;
  /** ISO-Zeitstempel (timestamptz). */
  unterschrieben_am: string;
  user_agent?: string | null;
  created_at?: string;
}

/**
 * Was die oeffentliche Seite `#/einweisung/<token>` von der RPC
 * `sige_einweisung_public` bekommt — NIE Teilnehmer, Unterschriften oder
 * Buchungsfelder.
 */
export interface EinweisungPublic {
  einweisungId: string;
  nummer: number;
  titel?: string | null;
  datum: string;
  uhrzeit?: string | null;
  ort?: string | null;
  durchfuehrenderName?: string | null;
  planNr?: string | null;
  planStand?: string | null;
  punkte: EinweisungGruppe[];
  projektName?: string | null;
  bauvorhabenAdresse?: string | null;
  bueroName?: string | null;
}

/**
 * Die EINE Quelle fuer den QR-Code auf Aushang, SiGe-Plan und Handbuch:
 * die juengste Einweisung des Projekts mit aktivem Link.
 */
export interface EinweisungQrQuelle {
  einweisungId: string;
  nummer: number;
  url: string;
  /** PNG-DataURL des QR-Codes. */
  dataUrl: string;
}
